/**
 * EditorContext.jsx
 * 
 * State management for the Book Editor using useReducer.
 * Handles all editor state including pages, selection, undo/redo.
 * 
 * Performance considerations:
 * - Memoized context value to prevent unnecessary re-renders
 * - Separate contexts for state and dispatch
 * - Shallow comparison for page updates
 */

import React, { createContext, useContext, useReducer, useMemo, useCallback } from 'react';

// ============================================
// INITIAL STATE
// ============================================

const initialState = {
  // Draft metadata
  draftId: null,
  status: 'loading', // 'loading' | 'ready' | 'saving' | 'error'
  error: null,
  
  // Book structure
  pages: [],
  
  // Selection state
  selectedPageId: null,
  selectedEntryId: null,
  
  // UI state
  isLayoutPickerOpen: false,
  isEntryPickerOpen: false,
  isDragging: false,
  
  // Undo/redo stacks
  undoStack: [],
  redoStack: [],
  
  // Dirty tracking for auto-save
  isDirty: false,
  lastSavedAt: null,
  
  // Entry cache (minimal data for display)
  entryCache: {},
};

// ============================================
// ACTION TYPES
// ============================================

export const ActionTypes = {
  // Initialization
  LOAD_DRAFT_START: 'LOAD_DRAFT_START',
  LOAD_DRAFT_SUCCESS: 'LOAD_DRAFT_SUCCESS',
  LOAD_DRAFT_ERROR: 'LOAD_DRAFT_ERROR',
  
  // Page operations
  SELECT_PAGE: 'SELECT_PAGE',
  REORDER_PAGES: 'REORDER_PAGES',
  UPDATE_PAGE_LAYOUT: 'UPDATE_PAGE_LAYOUT',
  ADD_PAGE: 'ADD_PAGE',
  DELETE_PAGE: 'DELETE_PAGE',
  DUPLICATE_PAGE: 'DUPLICATE_PAGE',
  
  // Entry operations
  ADD_ENTRY_TO_PAGE: 'ADD_ENTRY_TO_PAGE',
  REMOVE_ENTRY_FROM_PAGE: 'REMOVE_ENTRY_FROM_PAGE',
  UPDATE_PAGE_TEXT: 'UPDATE_PAGE_TEXT',
  
  // UI state
  TOGGLE_LAYOUT_PICKER: 'TOGGLE_LAYOUT_PICKER',
  TOGGLE_ENTRY_PICKER: 'TOGGLE_ENTRY_PICKER',
  SET_DRAGGING: 'SET_DRAGGING',
  
  // Undo/redo
  UNDO: 'UNDO',
  REDO: 'REDO',
  PUSH_UNDO: 'PUSH_UNDO',
  
  // Save state
  SAVE_START: 'SAVE_START',
  SAVE_SUCCESS: 'SAVE_SUCCESS',
  SAVE_ERROR: 'SAVE_ERROR',
  MARK_DIRTY: 'MARK_DIRTY',
  
  // Entry cache
  UPDATE_ENTRY_CACHE: 'UPDATE_ENTRY_CACHE',
};

// ============================================
// REDUCER
// ============================================

function editorReducer(state, action) {
  switch (action.type) {
    // ---- INITIALIZATION ----
    case ActionTypes.LOAD_DRAFT_START:
      return {
        ...state,
        status: 'loading',
        error: null,
      };
      
    case ActionTypes.LOAD_DRAFT_SUCCESS:
      return {
        ...state,
        status: 'ready',
        draftId: action.payload.draftId,
        pages: action.payload.pages,
        selectedPageId: action.payload.pages[0]?.id || null,
        undoStack: [],
        redoStack: [],
        isDirty: false,
        error: null,
      };
      
    case ActionTypes.LOAD_DRAFT_ERROR:
      return {
        ...state,
        status: 'error',
        error: action.payload.error,
      };
    
    // ---- PAGE OPERATIONS ----
    case ActionTypes.SELECT_PAGE:
      return {
        ...state,
        selectedPageId: action.payload.pageId,
        isLayoutPickerOpen: false,
        isEntryPickerOpen: false,
      };
      
    case ActionTypes.REORDER_PAGES: {
      const { sourceIndex, destinationIndex } = action.payload;
      const newPages = [...state.pages];
      const [removed] = newPages.splice(sourceIndex, 1);
      newPages.splice(destinationIndex, 0, removed);
      
      // Update sortOrder for all affected pages
      const updatedPages = newPages.map((page, index) => ({
        ...page,
        sortOrder: index,
      }));
      
      return {
        ...state,
        pages: updatedPages,
        isDirty: true,
      };
    }
    
    case ActionTypes.UPDATE_PAGE_LAYOUT: {
      const { pageId, layout } = action.payload;
      return {
        ...state,
        pages: state.pages.map(page =>
          page.id === pageId ? { ...page, layout } : page
        ),
        isDirty: true,
      };
    }
    
    case ActionTypes.ADD_PAGE: {
      const { afterPageId, pageData } = action.payload;
      const insertIndex = afterPageId
        ? state.pages.findIndex(p => p.id === afterPageId) + 1
        : state.pages.length;
      
      const newPage = {
        id: generatePageId(),
        type: 'content',
        layout: 'hero',
        entries: [],
        customText: null,
        sectionTitle: null,
        sortOrder: insertIndex,
        isLocked: false,
        ...pageData,
      };
      
      const newPages = [...state.pages];
      newPages.splice(insertIndex, 0, newPage);
      
      // Update sortOrder for subsequent pages
      const updatedPages = newPages.map((page, index) => ({
        ...page,
        sortOrder: index,
      }));
      
      return {
        ...state,
        pages: updatedPages,
        selectedPageId: newPage.id,
        isDirty: true,
      };
    }
    
    case ActionTypes.DELETE_PAGE: {
      const { pageId } = action.payload;
      const page = state.pages.find(p => p.id === pageId);
      
      // Prevent deleting locked pages
      if (page?.isLocked) {
        return state;
      }
      
      const newPages = state.pages.filter(p => p.id !== pageId);
      const deletedIndex = state.pages.findIndex(p => p.id === pageId);
      
      // Update sortOrder
      const updatedPages = newPages.map((page, index) => ({
        ...page,
        sortOrder: index,
      }));
      
      // Select adjacent page
      let newSelectedId = state.selectedPageId;
      if (state.selectedPageId === pageId) {
        newSelectedId = updatedPages[Math.min(deletedIndex, updatedPages.length - 1)]?.id || null;
      }
      
      return {
        ...state,
        pages: updatedPages,
        selectedPageId: newSelectedId,
        isDirty: true,
      };
    }
    
    case ActionTypes.DUPLICATE_PAGE: {
      const { pageId } = action.payload;
      const sourcePage = state.pages.find(p => p.id === pageId);
      
      if (!sourcePage || sourcePage.isLocked) {
        return state;
      }
      
      const sourceIndex = state.pages.findIndex(p => p.id === pageId);
      const newPage = {
        ...sourcePage,
        id: generatePageId(),
        isLocked: false,
        sortOrder: sourceIndex + 1,
      };
      
      const newPages = [...state.pages];
      newPages.splice(sourceIndex + 1, 0, newPage);
      
      const updatedPages = newPages.map((page, index) => ({
        ...page,
        sortOrder: index,
      }));
      
      return {
        ...state,
        pages: updatedPages,
        selectedPageId: newPage.id,
        isDirty: true,
      };
    }
    
    // ---- ENTRY OPERATIONS ----
    case ActionTypes.ADD_ENTRY_TO_PAGE: {
      const { pageId, entryId } = action.payload;
      return {
        ...state,
        pages: state.pages.map(page =>
          page.id === pageId
            ? { ...page, entries: [...page.entries, entryId] }
            : page
        ),
        isDirty: true,
      };
    }
    
    case ActionTypes.REMOVE_ENTRY_FROM_PAGE: {
      const { pageId, entryId } = action.payload;
      return {
        ...state,
        pages: state.pages.map(page =>
          page.id === pageId
            ? { ...page, entries: page.entries.filter(id => id !== entryId) }
            : page
        ),
        isDirty: true,
      };
    }
    
    case ActionTypes.UPDATE_PAGE_TEXT: {
      const { pageId, customText } = action.payload;
      return {
        ...state,
        pages: state.pages.map(page =>
          page.id === pageId ? { ...page, customText } : page
        ),
        isDirty: true,
      };
    }
    
    // ---- UI STATE ----
    case ActionTypes.TOGGLE_LAYOUT_PICKER:
      return {
        ...state,
        isLayoutPickerOpen: action.payload ?? !state.isLayoutPickerOpen,
      };
      
    case ActionTypes.TOGGLE_ENTRY_PICKER:
      return {
        ...state,
        isEntryPickerOpen: action.payload ?? !state.isEntryPickerOpen,
      };
      
    case ActionTypes.SET_DRAGGING:
      return {
        ...state,
        isDragging: action.payload,
      };
    
    // ---- UNDO/REDO ----
    case ActionTypes.PUSH_UNDO: {
      // Limit undo stack size
      const maxUndoSize = 50;
      const newUndoStack = [
        ...state.undoStack.slice(-maxUndoSize + 1),
        { pages: state.pages, selectedPageId: state.selectedPageId },
      ];
      return {
        ...state,
        undoStack: newUndoStack,
        redoStack: [], // Clear redo on new action
      };
    }
    
    case ActionTypes.UNDO: {
      if (state.undoStack.length === 0) return state;
      
      const previousState = state.undoStack[state.undoStack.length - 1];
      return {
        ...state,
        pages: previousState.pages,
        selectedPageId: previousState.selectedPageId,
        undoStack: state.undoStack.slice(0, -1),
        redoStack: [
          ...state.redoStack,
          { pages: state.pages, selectedPageId: state.selectedPageId },
        ],
        isDirty: true,
      };
    }
    
    case ActionTypes.REDO: {
      if (state.redoStack.length === 0) return state;
      
      const nextState = state.redoStack[state.redoStack.length - 1];
      return {
        ...state,
        pages: nextState.pages,
        selectedPageId: nextState.selectedPageId,
        redoStack: state.redoStack.slice(0, -1),
        undoStack: [
          ...state.undoStack,
          { pages: state.pages, selectedPageId: state.selectedPageId },
        ],
        isDirty: true,
      };
    }
    
    // ---- SAVE STATE ----
    case ActionTypes.SAVE_START:
      return {
        ...state,
        status: 'saving',
      };
      
    case ActionTypes.SAVE_SUCCESS:
      return {
        ...state,
        status: 'ready',
        isDirty: false,
        lastSavedAt: new Date(),
      };
      
    case ActionTypes.SAVE_ERROR:
      return {
        ...state,
        status: 'error',
        error: action.payload.error,
      };
      
    case ActionTypes.MARK_DIRTY:
      return {
        ...state,
        isDirty: true,
      };
    
    // ---- ENTRY CACHE ----
    case ActionTypes.UPDATE_ENTRY_CACHE:
      return {
        ...state,
        entryCache: {
          ...state.entryCache,
          ...action.payload.entries,
        },
      };
    
    default:
      return state;
  }
}

// ============================================
// HELPER FUNCTIONS
// ============================================

function generatePageId() {
  return 'page_' + Math.random().toString(36).substr(2, 9);
}

// ============================================
// CONTEXTS
// ============================================

const EditorStateContext = createContext(null);
const EditorDispatchContext = createContext(null);

// ============================================
// PROVIDER
// ============================================

export function EditorProvider({ children }) {
  const [state, dispatch] = useReducer(editorReducer, initialState);
  
  // Memoize the state context value
  const stateValue = useMemo(() => state, [state]);
  
  // Memoize dispatch with action creators
  const dispatchValue = useMemo(() => ({
    dispatch,
    
    // Action creators for cleaner API
    loadDraft: (draftId, pages) => {
      dispatch({ type: ActionTypes.LOAD_DRAFT_SUCCESS, payload: { draftId, pages } });
    },
    
    selectPage: (pageId) => {
      dispatch({ type: ActionTypes.SELECT_PAGE, payload: { pageId } });
    },
    
    reorderPages: (sourceIndex, destinationIndex) => {
      dispatch({ type: ActionTypes.PUSH_UNDO });
      dispatch({ type: ActionTypes.REORDER_PAGES, payload: { sourceIndex, destinationIndex } });
    },
    
    updatePageLayout: (pageId, layout) => {
      dispatch({ type: ActionTypes.PUSH_UNDO });
      dispatch({ type: ActionTypes.UPDATE_PAGE_LAYOUT, payload: { pageId, layout } });
    },
    
    addPage: (afterPageId, pageData) => {
      dispatch({ type: ActionTypes.PUSH_UNDO });
      dispatch({ type: ActionTypes.ADD_PAGE, payload: { afterPageId, pageData } });
    },
    
    deletePage: (pageId) => {
      dispatch({ type: ActionTypes.PUSH_UNDO });
      dispatch({ type: ActionTypes.DELETE_PAGE, payload: { pageId } });
    },
    
    duplicatePage: (pageId) => {
      dispatch({ type: ActionTypes.PUSH_UNDO });
      dispatch({ type: ActionTypes.DUPLICATE_PAGE, payload: { pageId } });
    },
    
    addEntryToPage: (pageId, entryId) => {
      dispatch({ type: ActionTypes.PUSH_UNDO });
      dispatch({ type: ActionTypes.ADD_ENTRY_TO_PAGE, payload: { pageId, entryId } });
    },
    
    removeEntryFromPage: (pageId, entryId) => {
      dispatch({ type: ActionTypes.PUSH_UNDO });
      dispatch({ type: ActionTypes.REMOVE_ENTRY_FROM_PAGE, payload: { pageId, entryId } });
    },
    
    updatePageText: (pageId, customText) => {
      dispatch({ type: ActionTypes.PUSH_UNDO });
      dispatch({ type: ActionTypes.UPDATE_PAGE_TEXT, payload: { pageId, customText } });
    },
    
    toggleLayoutPicker: (open) => {
      dispatch({ type: ActionTypes.TOGGLE_LAYOUT_PICKER, payload: open });
    },
    
    toggleEntryPicker: (open) => {
      dispatch({ type: ActionTypes.TOGGLE_ENTRY_PICKER, payload: open });
    },
    
    setDragging: (isDragging) => {
      dispatch({ type: ActionTypes.SET_DRAGGING, payload: isDragging });
    },
    
    undo: () => {
      dispatch({ type: ActionTypes.UNDO });
    },
    
    redo: () => {
      dispatch({ type: ActionTypes.REDO });
    },
    
    updateEntryCache: (entries) => {
      dispatch({ type: ActionTypes.UPDATE_ENTRY_CACHE, payload: { entries } });
    },
    
    setSaving: () => {
      dispatch({ type: ActionTypes.SAVE_START });
    },
    
    setSaveSuccess: () => {
      dispatch({ type: ActionTypes.SAVE_SUCCESS });
    },
    
    setSaveError: (error) => {
      dispatch({ type: ActionTypes.SAVE_ERROR, payload: { error } });
    },
  }), [dispatch]);
  
  return (
    <EditorStateContext.Provider value={stateValue}>
      <EditorDispatchContext.Provider value={dispatchValue}>
        {children}
      </EditorDispatchContext.Provider>
    </EditorStateContext.Provider>
  );
}

// ============================================
// HOOKS
// ============================================

export function useEditorState() {
  const context = useContext(EditorStateContext);
  if (context === null) {
    throw new Error('useEditorState must be used within an EditorProvider');
  }
  return context;
}

export function useEditorDispatch() {
  const context = useContext(EditorDispatchContext);
  if (context === null) {
    throw new Error('useEditorDispatch must be used within an EditorProvider');
  }
  return context;
}

// Combined hook for convenience
export function useEditor() {
  return {
    state: useEditorState(),
    ...useEditorDispatch(),
  };
}

// Selector hooks for specific state slices (performance optimization)
export function useSelectedPage() {
  const state = useEditorState();
  return useMemo(() => 
    state.pages.find(p => p.id === state.selectedPageId) || null,
    [state.pages, state.selectedPageId]
  );
}

export function useCanUndo() {
  const state = useEditorState();
  return state.undoStack.length > 0;
}

export function useCanRedo() {
  const state = useEditorState();
  return state.redoStack.length > 0;
}

export default EditorProvider;
